import { CheckCircle2, ExternalLink, Plus } from 'lucide-react';

import { downloadUrl } from '@/api/client';
import type { Sale } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { money } from '@/lib/utils';

/** Shown right after checkout — the invoice number is the thing to confirm fastest. */
export function BillReceiptDialog({
  sale,
  onOpenChange,
  onNewBill,
}: {
  sale: Sale | null;
  onOpenChange: (open: boolean) => void;
  onNewBill: () => void;
}) {
  return (
    <Dialog open={sale !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        {sale && (
          <>
            <DialogHeader className="items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-status-ok/15 text-status-ok">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <DialogTitle>Bill generated</DialogTitle>
              <p className="font-mono text-sm text-muted-foreground">{sale.invoiceNo}</p>
            </DialogHeader>

            <div className="rounded-lg border border-border p-4">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Total charged</span>
                <span className="tabular font-display text-3xl">{money(sale.totalAmount)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                <span>{sale.items.length} item(s)</span>
                <span>{sale.paymentMode}</span>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" asChild>
                <a href={downloadUrl(`/api/bills/${sale.invoiceNo}/pdf`)} target="_blank" rel="noreferrer">
                  <ExternalLink /> View / print bill
                </a>
              </Button>
              <Button onClick={onNewBill}>
                <Plus /> New bill
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
